/**
 * ImpersonationBanner.tsx
 *
 * Persistent red banner shown at the top of every page when the user
 * is browsing in an admin impersonation (read-only preview) session.
 *
 * Reads the impersonation token from sessionStorage.
 * Provides an "Exit preview" button that clears the token and returns
 * the admin to /admin.
 */

import { useEffect, useState } from 'react'
import { ShieldAlert, X, Clock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface ImpersonationInfo {
  role:      string
  expiresAt: Date
}

function parseImpersonationToken(token: string): ImpersonationInfo | null {
  try {
    const [, payloadB64] = token.split('.')
    if (!payloadB64) return null
    const payload = JSON.parse(atob(payloadB64))
    if (!payload.impersonation) return null
    return {
      role:      payload.role as string,
      expiresAt: new Date(payload.exp * 1000),
    }
  } catch {
    return null
  }
}

function useCountdown(expiresAt: Date): string {
  const [remaining, setRemaining] = useState('')

  useEffect(() => {
    function update() {
      const diff = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000))
      const m    = Math.floor(diff / 60)
      const s    = diff % 60
      setRemaining(`${m}:${String(s).padStart(2, '0')}`)
    }
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [expiresAt])

  return remaining
}

export function useIsImpersonating(): boolean {
  const token = sessionStorage.getItem('impersonation_token')
  if (!token) return false
  const info = parseImpersonationToken(token)
  return !!info && info.expiresAt > new Date()
}

export default function ImpersonationBanner() {
  const navigate = useNavigate()
  const token    = sessionStorage.getItem('impersonation_token')
  const info     = token ? parseImpersonationToken(token) : null
  const countdown = useCountdown(info?.expiresAt ?? new Date())

  if (!info || info.expiresAt <= new Date()) return null

  function exitPreview() {
    sessionStorage.removeItem('impersonation_token')
    sessionStorage.removeItem('impersonation_role')
    // Admin's real token is already in authStore  -  no reload needed.
    // Navigate directly back to admin. If you also have a student session open
    // in *this same tab*, log out of student first (/student → sign out) then re-enter admin.
    navigate('/admin', { replace: true })
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-3 px-4 py-2 text-sm font-bold"
      style={{ background: '#DC2626', color: '#fff' }}
    >
      <div className="flex items-center gap-2">
        <ShieldAlert size={16} />
        <span>
          READ-ONLY PREVIEW  -  viewing as{' '}
          <strong className="uppercase">{info.role.replace('_', ' ')}</strong>
          {' '}· No changes will be saved
        </span>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="flex items-center gap-1 bg-alert-700 px-2 py-0.5 rounded text-xs">
          <Clock size={12} />
          {countdown}
        </div>
        <button
          onClick={exitPreview}
          className="flex items-center gap-1 bg-white text-alert-600 px-3 py-1 rounded-full text-xs font-black hover:bg-alert-50 transition-colors"
        >
          <X size={12} />
          Exit preview
        </button>
      </div>
    </div>
  )
}
